import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Trash2, Sparkles, Wrench, ChevronDown, Copy, Check, Maximize2, Minimize2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ACTIVE_PREFIXES = [
  "/partner/portfoliomanagement",
  "/partner/benchmark-maintenance",
];

const SUGGESTIONS = [
  "Which Invesco BM4 holdings are unmapped?",
  "Show P3 positioning vs benchmark",
  "Summarise BM3 sector drift",
  "List fund houses missing allocations",
];

const ACTION_TOOLS = new Set(["recompute_bm_allocations", "backfill_missing_bm_mappings"]);

interface Msg {
  id: string;
  role: "user" | "assistant" | "tool";
  content: string | null;
  tool_calls: any;
  tool_name: string | null;
  created_at: string;
}

function fmtTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-1 rounded"
      title="Copy"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

const markdownComponents = {
  table: ({ node, ...props }: any) => (
    <div className="my-2 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs" {...props} />
    </div>
  ),
  thead: (p: any) => <thead className="bg-muted/60" {...p} />,
  th: (p: any) => <th className="px-2 py-1.5 text-left font-semibold border-b border-border" {...p} />,
  td: (p: any) => <td className="px-2 py-1 border-b border-border/50 align-top" {...p} />,
  code: ({ inline, children, ...props }: any) =>
    inline ? (
      <code className="px-1 py-0.5 rounded bg-muted text-[0.85em] font-mono" {...props}>{children}</code>
    ) : (
      <code className="block p-2 rounded bg-muted text-[0.8em] font-mono overflow-x-auto" {...props}>{children}</code>
    ),
  a: (p: any) => <a className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  ul: (p: any) => <ul className="list-disc pl-5 space-y-0.5 my-1.5" {...p} />,
  ol: (p: any) => <ol className="list-decimal pl-5 space-y-0.5 my-1.5" {...p} />,
  li: (p: any) => <li className="leading-snug" {...p} />,
  h1: (p: any) => <h3 className="text-sm font-semibold mt-2 mb-1" {...p} />,
  h2: (p: any) => <h4 className="text-sm font-semibold mt-2 mb-1" {...p} />,
  h3: (p: any) => <h5 className="text-[13px] font-semibold mt-1.5 mb-0.5" {...p} />,
  p: (p: any) => <p className="my-1 leading-relaxed" {...p} />,
  blockquote: (p: any) => <blockquote className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground my-1.5" {...p} />,
  hr: () => <hr className="my-2 border-border" />,
};

function Markdown({ text }: { text: string }) {
  return (
    <div className="text-sm text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
    </div>
  );
}

export default function CBAMAssistant() {
  const { user, staffMember } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isActiveRoute = ACTIVE_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isSuperadmin = staffMember?.role_level === "superadmin";

  useEffect(() => {
    if (!user || !open) return;
    supabase
      .from("cbam_assistant_messages")
      .select("id, role, content, tool_calls, tool_name, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .limit(200)
      .then(({ data }) => setMessages((data || []) as Msg[]));
  }, [user, open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, sending]);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  }, [input]);

  // Group consecutive tool messages under their preceding assistant tool_calls message
  const grouped = useMemo(() => {
    const out: Array<Msg & { tools?: Msg[] }> = [];
    for (const m of messages) {
      if (m.role === "tool" && out.length && out[out.length - 1].role === "assistant" && (out[out.length - 1] as any).tool_calls) {
        const last = out[out.length - 1] as any;
        last.tools = [...(last.tools || []), m];
      } else {
        out.push({ ...m });
      }
    }
    return out;
  }, [messages]);

  if (!user || !isActiveRoute) return null;

  const submit = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const optimistic: Msg = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
      tool_calls: null,
      tool_name: null,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    try {
      const { data, error } = await supabase.functions.invoke("cbam-assistant", {
        body: { message: text, route: location.pathname },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const { data: fresh } = await supabase
        .from("cbam_assistant_messages")
        .select("id, role, content, tool_calls, tool_name, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(200);
      setMessages((fresh || []) as Msg[]);
    } catch (e: any) {
      toast.error(e?.message || "Assistant error");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setInput(text);
    } finally {
      setSending(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const clearChat = async () => {
    if (!confirm("Clear your entire CBAM Assistant conversation?")) return;
    await supabase.from("cbam_assistant_messages").delete().eq("user_id", user.id);
    setMessages([]);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="group fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center ring-1 ring-primary/20"
          aria-label="Open CBAM Assistant"
        >
          <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-background" />
        </button>
      )}

      {open && (
        <div
          className={cn(
            "fixed z-50 bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200",
            expanded
              ? "bottom-6 right-6 w-[min(900px,calc(100vw-3rem))] h-[min(850px,calc(100vh-6rem))]"
              : "bottom-6 right-6 w-[440px] max-w-[calc(100vw-2rem)] h-[680px] max-h-[calc(100vh-3rem)]"
          )}
        >
          {/* Header */}
          <header className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-muted/60 to-muted/20 backdrop-blur">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-sm leading-tight">CBAM Assistant</h3>
                <p className="text-[11px] text-muted-foreground leading-tight truncate">
                  Portfolios & Benchmarks
                  {isSuperadmin && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-[9px] font-semibold uppercase tracking-wide">
                      Beta
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpanded((e) => !e)} title={expanded ? "Shrink" : "Expand"}>
                {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={clearChat} title="Clear conversation">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-background to-muted/10">
            {messages.length === 0 && !sending && (
              <div className="py-6 space-y-4">
                <div className="text-center space-y-1.5">
                  <div className="mx-auto h-12 w-12 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">How can I help today?</p>
                  <p className="text-xs text-muted-foreground px-4">
                    Ask about portfolios, benchmarks, holdings, or sector mappings.
                  </p>
                </div>
                <div className="grid gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => submit(s)}
                      className="text-left text-xs px-3 py-2 rounded-lg border border-border/60 bg-card hover:bg-accent hover:border-primary/40 transition-colors"
                    >
                      <span className="text-muted-foreground mr-1.5">›</span>{s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {grouped.map((m: any) => {
              if (m.role === "user") {
                return (
                  <div key={m.id} className="group flex flex-col items-end gap-0.5">
                    <div className="max-w-[85%] bg-primary text-primary-foreground rounded-2xl rounded-br-md px-3.5 py-2 text-sm whitespace-pre-wrap shadow-sm">
                      {m.content}
                    </div>
                    <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity px-1">
                      {fmtTime(m.created_at)}
                    </span>
                  </div>
                );
              }

              if (m.role === "assistant") {
                const toolCalls = m.tool_calls as any[] | null;
                const tools: Msg[] = m.tools || [];
                return (
                  <div key={m.id} className="group flex gap-2.5">
                    <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      {toolCalls && toolCalls.length > 0 && (
                        <details className="group/tool rounded-lg border border-border/60 bg-muted/30 text-xs overflow-hidden">
                          <summary className="cursor-pointer px-2.5 py-1.5 flex items-center gap-1.5 hover:bg-muted/60 list-none [&::-webkit-details-marker]:hidden">
                            <Wrench className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium">
                              {toolCalls.length} tool {toolCalls.length === 1 ? "call" : "calls"}
                            </span>
                            <div className="flex gap-1 ml-1">
                              {toolCalls.map((tc: any, i: number) => (
                                <span
                                  key={i}
                                  className={cn(
                                    "px-1.5 py-0.5 rounded text-[10px] font-mono",
                                    ACTION_TOOLS.has(tc.function?.name)
                                      ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                      : "bg-background text-muted-foreground"
                                  )}
                                >
                                  {tc.function?.name}
                                  {ACTION_TOOLS.has(tc.function?.name) && " ⚡"}
                                </span>
                              ))}
                            </div>
                            <ChevronDown className="h-3 w-3 ml-auto text-muted-foreground transition-transform group-open/tool:rotate-180" />
                          </summary>
                          <div className="px-2.5 py-2 border-t border-border/60 space-y-2 bg-background/40">
                            {toolCalls.map((tc: any, i: number) => {
                              const result = tools[i];
                              let args = "";
                              try { args = JSON.stringify(JSON.parse(tc.function?.arguments || "{}"), null, 2); } catch { args = tc.function?.arguments || ""; }
                              return (
                                <div key={i} className="space-y-1">
                                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Arguments</div>
                                  <pre className="text-[10px] bg-muted/50 rounded p-1.5 overflow-x-auto">{args}</pre>
                                  {result && (
                                    <>
                                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">Result</div>
                                      <pre className="text-[10px] bg-muted/50 rounded p-1.5 overflow-x-auto max-h-40">{result.content}</pre>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                      {m.content && (
                        <div className="relative">
                          <Markdown text={m.content} />
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                              {fmtTime(m.created_at)}
                            </span>
                            <CopyButton text={m.content} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return null;
            })}

            {sending && (
              <div className="flex gap-2.5">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 text-primary flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                </div>
                <div className="flex items-center gap-1 pt-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t bg-background p-2.5">
            <div className="relative flex items-end gap-2 rounded-xl border border-border focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/10 bg-background px-2 py-1.5 transition-all">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder="Ask anything…   ⏎ to send · ⇧⏎ for newline"
                rows={1}
                className="resize-none text-sm min-h-[28px] max-h-[160px] border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-1 py-1 bg-transparent"
                disabled={sending}
              />
              <Button
                size="icon"
                onClick={() => submit()}
                disabled={sending || !input.trim()}
                className="h-8 w-8 flex-shrink-0 rounded-lg"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const CBAM_ASSISTANT_PREFIXES = ACTIVE_PREFIXES;
